import {
  DetailedFreeTrial,
  FlyoutFreeTrialStatus as FreeTrialStatus,
  useFlyOutProductsBasedOnSelectedCloud,
} from '@labrav/marketplace'
import { TextField } from '@mui/material'
import {
  ButtonV2,
  DateTime,
  ModalConfirm,
  ModalSizeVariants,
  TableComponent,
} from '@labrav/react-components'
import { isEmpty } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  LoadingTypes,
  isLoading,
  useCurrentCloudProvider,
} from '@labrav/common'
import {
  clearProductsListing,
  getProducts,
  patchFreeTrialOffer,
  updateFreeTrialOfferId,
} from '../../../flyout/modules/productsListing/actions'
import { useDispatch, useSelector } from '../../../store'
import { Product } from '../../../flyout/modules/productsListing/reducer'
import { useStyles } from './FreeTrialsAdminUI.styles'
import { actionTypeWrapper } from '../../../common/utils/actionTypeWrapper'
export const mappingFreeTrialAdminUI = {
  'Offer name': 'cloudFreeTrialOfferName',
  'Product name': 'productName',
  'Labra free trial offer ID': 'labraFreeTrialId',
  'Enabled at': 'freeTrialEnabledAt',
  'AWS free trial offer ID': 'cloudFreeTrialOfferId',
  Actions: 'actions',
}
export const FreeTrialsAdminUI: React.FC = () => {
  const { classes } = useStyles()
  const { partner_id: partnerId } = useParams<{ partner_id: string }>()
  const { products: productRows } = useFlyOutProductsBasedOnSelectedCloud()

  const products =
    productRows.filter(
      product =>
        !isEmpty(product.freeTrial?.cloudFreeTrialOfferName) &&
        product.freeTrial?.freeTrialStatus !==
          FreeTrialStatus.FREE_TRIAL_DEACTIVATED
    ) || []
  const selectedCloudTypeProductListing = useCurrentCloudProvider()

  const dispatch = useDispatch()
  useEffect(() => {
    if (!isEmpty(partnerId)) {
      /* istanbul ignore next */
      if (isEmpty(products)) {
        dispatch(getProducts(partnerId!, selectedCloudTypeProductListing))
      }
      return () => {
        dispatch(
          actionTypeWrapper(
            selectedCloudTypeProductListing,
            clearProductsListing()
          )
        )
      }
    }
  }, [partnerId, selectedCloudTypeProductListing])
  const [selectedProduct, setSelectedProduct] = useState<Product>({} as Product)
  const [openModal, setOpenModal] = useState<boolean>(false)
  const handleInputChange = useCallback(
    (productId: string) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        dispatch(
          actionTypeWrapper(
            selectedCloudTypeProductListing,
            updateFreeTrialOfferId(productId, event.target.value || '')
          )
        )
      },
    [products]
  )
  const updateFreeTrialOfferData = useCallback(
    (productId: string) => async () => {
      await dispatch(patchFreeTrialOffer(productId, partnerId!))
      await dispatch(getProducts(partnerId!, selectedCloudTypeProductListing))
    },
    [products]
  )
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const viewFreeTrialDetailedOffer = useCallback(
    (product: Product) => () => {
      setSelectedProduct(product)
      setOpenModal(true)
    },
    [products]
  )
  const handleClose = useCallback(() => {
    setOpenModal(false)
  }, [openModal])
  const customObject = products.map(product => {
    return {
      id: product.productId,
      freeTrialEnabledAt: !isEmpty(product?.freeTrial?.freeTrialEnabledAt || '')
        ? ((
            <DateTime date={product?.freeTrial?.freeTrialEnabledAt || ''} />
          ) as never)
        : '-',
      cloudFreeTrialOfferName: product.freeTrial?.cloudFreeTrialOfferName || '',
      cloudFreeTrialOfferId: (
        <TextField
          onChange={handleInputChange(product.productId)}
          key={'cloudFreeTrialOfferId'}
          label={'Enter AWS free trial offer ID'}
          placeholder="N/A"
          variant="standard"
          defaultValue={product.freeTrial?.cloudFreeTrialOfferId || ''}
          className={`${classes.resizeFontTextField} ${classes.defaultValueWidth}`}
          slotProps={{
            htmlInput: {
              'data-testid': `${product.productId}-offer-id`,
              'data-id': `${product.productId}-offer-id`,
            },
          }}
        />
      ) as never,
      labraFreeTrialId: product.freeTrial?.labraFreeTrialId || '',
      productName: product.productName,
      actions: (
        <div className={classes.buttonContainer}>
          <ButtonV2
            onClick={updateFreeTrialOfferData(product.productId)}
            className={classes.submitButton}
            data-testid={`${product.productId}-submit`}
            disabled={loading}
          >
            Submit
          </ButtonV2>
          <ButtonV2
            onClick={viewFreeTrialDetailedOffer(product)}
            className={classes.submitButton}
            data-testid={`${product.productId}-view-details`}
            disabled={loading}
          >
            Details
          </ButtonV2>
        </div>
      ) as never,
    }
  })
  const noOp = useCallback(() => void 0, [])
  const selectedDimensions = useMemo(() => {
    return (
      (selectedProduct?.productDimensions || []).filter(
        dimension => dimension?.freeTrial?.freeTrial
      ) || []
    ).map(dimension => ({
      productDimensionId: dimension.id,
      freeTrial: {
        freeTrial: dimension?.freeTrial?.freeTrial || false,
        quantity: Number(dimension?.freeTrial?.quantity),
      },
    }))
  }, [selectedProduct])
  const selectedFreeTrialProduct = useMemo(
    () => ({
      productId: selectedProduct?.productId || '',
      eulaType: selectedProduct?.freeTrial?.eulaType || '',
      eulaFile: selectedProduct?.freeTrial?.eulaFiles || [],
      freeTrial: {
        ...selectedProduct?.freeTrial,
      },
      productDimensions: [...selectedDimensions],
    }),
    [selectedDimensions]
  )
  return (
    <div data-testid="free-trials" className={classes.container}>
      {' '}
      <TableComponent
        mapping={mappingFreeTrialAdminUI}
        objects={customObject}
        component="div"
        hidePagination={true}
        onChange={noOp}
        onClick={noOp}
        onChangeRowsPerPage={noOp}
        noHover={true}
        rowsPerPageOptions={[]}
        pageNumber={0}
        count={0}
        rowsPerPage={0}
      />
      <ModalConfirm
        title={'Preview'}
        subTitle={
          <div className={classes.reviewRoot}>
            <DetailedFreeTrial
              productFreeTrial={selectedFreeTrialProduct}
              product={selectedProduct}
            />
          </div>
        }
        content={''}
        open={openModal}
        titleAcceptButton={'Okay'}
        onAccept={handleClose}
        onDecline={handleClose}
        variant={ModalSizeVariants.medium}
        hideCancelButton={true}
      />
    </div>
  )
}
